import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NeatGradient } from '@firecms/neat';

export const DEFAULT_NEAT_CONFIG = {
  colors: [
    { color: '#5B4C81', enabled: true },
    { color: '#18181C', enabled: true },
    { color: '#18181C', enabled: true },
    { color: '#2C485E', enabled: true },
    { color: '#2C485E', enabled: true },
    { color: '#A8E6CF', enabled: false },
  ],
  speed: 4,
  horizontalPressure: 3,
  verticalPressure: 5,
  waveFrequencyX: 0,
  waveFrequencyY: 0,
  waveAmplitude: 0,
  shadows: 0,
  highlights: 7,
  colorBrightness: 1,
  colorSaturation: 8,
  wireframe: false,
  colorBlending: 4,
  backgroundColor: '#1A1A1E',
  backgroundAlpha: 1,
  grainScale: 29,
  grainSparsity: 0.06,
  grainIntensity: 0.2,
  grainSpeed: 0,
  resolution: 0.3,
  yOffset: 0,
  yOffsetWaveMultiplier: 1.5,
  yOffsetColorMultiplier: 1.8,
  yOffsetFlowMultiplier: 2,
  flowDistortionA: 5,
  flowDistortionB: 7.7,
  flowScale: 2.6,
  flowEase: 0.36,
  flowEnabled: false,
  vignetteIntensity: 0,
  vignetteRadius: 0.8,
};

// Bake each static gradient at its container's actual pixel size (× DPR up to 2)
// and cache the JPEG by config+size. Cache prefix bumped to v5 to invalidate any
// previous low-res or mis-aspected entries.
const CACHE_PREFIX = 'neat-cache-v5-';
const MAX_DPR = 2;
const MAX_DIM = 2400;
const MIN_DIM = 64;

function hashConfig(obj) {
  const s = JSON.stringify(obj);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function readCache(key) {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(CACHE_PREFIX + key) : null; }
  catch { return null; }
}
function writeCache(key, dataUrl) {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(CACHE_PREFIX + key, dataUrl); }
  catch { /* quota exceeded — silently skip */ }
}

// In-memory session cache so multiple <NeatBackground> instances with the same key
// don't each hit localStorage on every navigation.
const memoryCache = new Map();
function readMem(key) { return memoryCache.get(key) || null; }
function writeMem(key, dataUrl) { memoryCache.set(key, dataUrl); }

function computeCaptureSize(rect) {
  const dpr = Math.min(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1, MAX_DPR);
  // Snap CSS dimensions to a 16px grid so subpixel layout jitter between
  // refreshes (e.g. fractional pixels from getBoundingClientRect) doesn't
  // produce a fresh cache key on every load. The captured image stretches to
  // 100%/100% via objectFit: fill, so a few px difference is invisible.
  const cssW = Math.max(MIN_DIM, Math.round(rect.width / 16) * 16);
  const cssH = Math.max(MIN_DIM, Math.round(rect.height / 16) * 16);
  let w = Math.round(cssW * dpr);
  let h = Math.round(cssH * dpr);
  if (w > MAX_DIM) { const s = MAX_DIM / w; w = MAX_DIM; h = Math.round(h * s); }
  if (h > MAX_DIM) { const s = MAX_DIM / h; h = MAX_DIM; w = Math.round(w * s); }
  w = Math.max(w, MIN_DIM);
  h = Math.max(h, MIN_DIM);
  return { w, h };
}

// Render one frame of the gradient into an offscreen canvas sized (w × h) at
// resolution 1, capture as a JPEG data URL, tear down the WebGL context.
function bakeStaticGradient(config, w, h) {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') { resolve(null); return; }
    const offscreen = document.createElement('canvas');
    offscreen.width = w;
    offscreen.height = h;
    offscreen.style.position = 'fixed';
    offscreen.style.left = '-99999px';
    offscreen.style.top = '0';
    offscreen.style.width = `${w}px`;
    offscreen.style.height = `${h}px`;
    offscreen.style.pointerEvents = 'none';
    document.body.appendChild(offscreen);

    const gradient = new NeatGradient({ ref: offscreen, ...config, resolution: 1 });

    requestAnimationFrame(() => requestAnimationFrame(() => {
      let dataUrl = null;
      try { dataUrl = offscreen.toDataURL('image/jpeg', 0.92); }
      catch { /* tainted canvas — skip */ }
      try { gradient.destroy(); } catch { /* ignore */ }
      offscreen.parentNode && offscreen.parentNode.removeChild(offscreen);
      resolve(dataUrl);
    }));
  });
}

// Serialize bakes across all NeatBackground instances. Without this, every
// instance schedules its own bake in the same idle slot and they pile onto the
// main thread/GPU at once — causing visible jank. Each entry waits for the
// previous to finish + a small breathing window between them.
let bakeQueueTail = Promise.resolve();
function queueBake(config, w, h) {
  const next = bakeQueueTail
    .then(() => bakeStaticGradient(config, w, h))
    .then((result) => new Promise((res) => setTimeout(() => res(result), 16)));
  // Don't let a rejection stall the chain.
  bakeQueueTail = next.catch(() => {});
  return next;
}

// Whether this device should run the live WebGL gradient at all. Weak machines,
// data-saver and reduced-motion users keep the static placeholder instead.
function canAnimate() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const nav = window.navigator || {};
  if (nav.connection && nav.connection.saveData) return false;
  if (nav.hardwareConcurrency && nav.hardwareConcurrency <= 2) return false;
  if (nav.deviceMemory && nav.deviceMemory <= 2) return false;
  return true;
}

// Seconds over which the live gradient eases from standstill to full speed once
// it takes over from the placeholder, so the motion starts imperceptibly instead
// of switching on.
const PLACEHOLDER_RAMP_MS = 2500;

// `placeholder` (animated gradients only): { src, width, height } — an image of
// the gradient's first frame (fixed `seed`) rendered on a width×height canvas,
// painted from the very first frame of the page. The live canvas renders at that
// same fixed width×height (the pattern depends on the canvas's pixel size) and is
// stretched to the box exactly like the image, so both are the same picture. It
// starts frozen on that frame behind the image, takes over once it has drawn,
// then eases into motion. Devices that shouldn't animate (see canAnimate) keep
// the image.
export default function NeatBackground({ config, style, placeholder }) {
  const wrapperRef = useRef(null);
  const boxRef = useRef(null);
  const [boxSize, setBoxSize] = useState(null);
  const mergedConfig = { ...DEFAULT_NEAT_CONFIG, ...(config || {}) };
  const isStatic = mergedConfig.speed === 0;
  const configKey = isStatic ? hashConfig(mergedConfig) : null;

  const [staticSrc, setStaticSrc] = useState(null);
  // For animated gradients: keep the canvas invisible for the first few rendered
  // frames so the browser doesn't flash the low-res shader start-up state. Fades
  // in smoothly once the shader is producing a stable image.
  const [animReady, setAnimReady] = useState(false);

  // ---- Animated: run NeatGradient directly on the visible canvas ----
  useEffect(() => {
    if (isStatic || !wrapperRef.current) return;
    if (placeholder && !canAnimate()) return;
    const canvas = wrapperRef.current;
    let gradient;
    try {
      // With a placeholder, start frozen (speed 0) on the seed frame the
      // placeholder shows; motion starts at the swap below.
      gradient = new NeatGradient({ ref: canvas, ...mergedConfig, ...(placeholder ? { speed: 0 } : {}) });
    } catch {
      return; // no WebGL — the placeholder (if any) stays
    }

    if (placeholder) {
      // Neat draws its first frames into the default 300x150 buffer and only
      // resizes it to the CSS size ~100ms later (debounced ResizeObserver) — and
      // the framing changes with it. So wait for the real size, let it draw a
      // couple of frames, then swap in and start moving: the canvas then shows
      // exactly the placeholder's frame at the moment it takes over.
      let raf;
      const started = performance.now();
      const reveal = () => {
        setAnimReady(true);
        // Ease in the motion (smoothstep: zero slope at the start).
        const rampStart = performance.now();
        const ramp = () => {
          const t = Math.min(1, (performance.now() - rampStart) / PLACEHOLDER_RAMP_MS);
          gradient.speed = mergedConfig.speed * t * t * (3 - 2 * t);
          if (t < 1) raf = requestAnimationFrame(ramp);
        };
        raf = requestAnimationFrame(ramp);
      };
      const waitSized = () => {
        const sized = canvas.width === canvas.clientWidth && canvas.height === canvas.clientHeight;
        if (sized || performance.now() - started > 3000) {
          raf = requestAnimationFrame(() => { raf = requestAnimationFrame(reveal); });
        } else {
          raf = requestAnimationFrame(waitSized);
        }
      };
      raf = requestAnimationFrame(waitSized);
      return () => {
        cancelAnimationFrame(raf);
        gradient.destroy();
      };
    }

    // Don't start the fade-in until the shader has actually painted a couple of
    // stable frames — otherwise the 1.4s opacity transition runs against a blank
    // canvas and the real gradient "pops" in at the end (the on/off flash). We
    // wait two animation frames (guarantees ≥1 rendered frame) plus a short,
    // deliberate delay so the reveal reads as an intentional, smooth fade-in.
    let raf2;
    let timer;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        timer = setTimeout(() => setAnimReady(true), 250);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
      gradient.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Placeholder: track the box size to stretch the fixed-size canvas ----
  useLayoutEffect(() => {
    if (!placeholder || !boxRef.current) return undefined;
    const measure = () => {
      const el = boxRef.current;
      if (!el) return;
      // Fractional size (clientWidth rounds), so the canvas lines up exactly
      // with the image.
      const r = el.getBoundingClientRect();
      setBoxSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(boxRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Static: SYNCHRONOUS cache lookup before first paint ----
  // useLayoutEffect measures + swaps to <img> before the browser paints, so cached
  // gradients appear on the very first visible frame (no canvas flicker).
  useLayoutEffect(() => {
    if (!isStatic || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const { w, h } = computeCaptureSize(rect);
    const key = `${configKey}-${w}x${h}`;

    // Memory first (fastest), then localStorage.
    const cached = readMem(key) || readCache(key);
    if (cached) {
      writeMem(key, cached);
      setStaticSrc(cached);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Static: if no cache hit, bake when the section approaches the viewport ----
  // Sections far below the fold skip the bake entirely on initial load — they
  // only kick off when the user scrolls near them. This keeps the first second
  // of page life from being clogged with WebGL bakes for off-screen content.
  useEffect(() => {
    if (!isStatic || staticSrc || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const { w, h } = computeCaptureSize(rect);
    const key = `${configKey}-${w}x${h}`;
    if (readMem(key) || readCache(key)) return; // layout effect will pick it up

    let cancelled = false;
    const startBake = () => {
      const schedule = (typeof window !== 'undefined' && window.requestIdleCallback)
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 50);
      schedule(() => {
        if (cancelled) return;
        queueBake(mergedConfig, w, h).then((dataUrl) => {
          if (cancelled || !dataUrl) return;
          writeMem(key, dataUrl);
          writeCache(key, dataUrl);
          setStaticSrc(dataUrl);
        });
      });
    };

    // Trigger when the element gets within 600px of the viewport — gives the
    // gradient time to bake before the user scrolls into view, while keeping
    // far-below-fold sections out of the initial-load work entirely.
    const obs = new IntersectionObserver(
      (entries) => {
        if (cancelled) return;
        if (entries.some((e) => e.isIntersecting)) {
          obs.disconnect();
          startBake();
        }
      },
      { rootMargin: '600px 0px' },
    );
    obs.observe(wrapperRef.current);
    return () => { cancelled = true; obs.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticSrc]);

  const sharedStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    pointerEvents: 'none',
    ...style,
  };

  if (isStatic && staticSrc) {
    return (
      <img
        ref={wrapperRef}
        src={staticSrc}
        alt=""
        aria-hidden="true"
        style={{ ...sharedStyle, objectFit: 'fill', display: 'block' }}
      />
    );
  }

  if (!isStatic && placeholder) {
    return (
      <div ref={boxRef} aria-hidden="true" style={{ ...sharedStyle, overflow: 'hidden' }}>
        <img
          src={placeholder.src}
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
        />
        <canvas
          ref={wrapperRef}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            // Fixed render size (see `placeholder`), stretched to the box.
            width: `${placeholder.width}px`,
            height: `${placeholder.height}px`,
            transformOrigin: '0 0',
            transform: boxSize
              ? `scale(${boxSize.w / placeholder.width}, ${boxSize.h / placeholder.height})`
              : 'none',
            // Same picture as the image underneath at the moment it takes over,
            // so this only softens the swap.
            opacity: animReady ? 1 : 0,
            transition: 'opacity 0.3s ease-out',
          }}
        />
      </div>
    );
  }

  // Canvas rendered:
  //   - for animated gradients (NeatGradient attaches here)
  //   - briefly, only on first-ever visit, while a static bake is in-flight
  return (
    <canvas
      ref={wrapperRef}
      aria-hidden="true"
      style={{
        ...sharedStyle,
        // Animated case: canvas starts invisible + slightly scaled up, then eases in.
        // Static case: canvas is only on-screen during the initial bake; leave opaque.
        opacity: !isStatic && !animReady ? 0 : 1,
        transform: !isStatic && !animReady ? 'scale(1.04)' : 'scale(1)',
        transformOrigin: 'center',
        transition:
          'opacity 4s cubic-bezier(0.25, 0.1, 0.25, 1), ' +
          'transform 4.2s cubic-bezier(0.25, 0.1, 0.25, 1)',
        willChange: 'opacity, transform',
      }}
    />
  );
}
